import { cva, type VariantProps } from "class-variance-authority";
import { useState, type ComponentPropsWithRef, type ReactNode } from "react";

import { cn } from "../lib/cn";

const avatarVariants = cva(
  "relative inline-flex shrink-0 items-center justify-center overflow-hidden bg-surface-sunken font-medium text-fg-muted select-none",
  {
    variants: {
      size: {
        xs: "size-6 text-[10px]",
        sm: "size-8 text-xs",
        md: "size-10 text-sm",
        lg: "size-14 text-base",
        xl: "size-20 text-xl",
      },
      shape: {
        circle: "rounded-full",
        square: "rounded-lg",
      },
    },
    defaultVariants: { size: "md", shape: "circle" },
  },
);

export interface AvatarProps
  extends Omit<ComponentPropsWithRef<"span">, "children">,
    VariantProps<typeof avatarVariants> {
  src?: string;
  /** Nom complet. Sert d'alternative textuelle ET de source aux initiales. */
  name?: string;
  /** Contenu de repli explicite (icône) si `name` n'a pas de sens. */
  fallback?: ReactNode;
  /** Pastille de présence posée en bas à droite. */
  status?: "online" | "busy" | "offline";
}

/** « Marie Dupont » → « MD » ; « Marie » → « MA ». */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return (parts[0] ?? "").slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts.at(-1)?.[0] ?? "")).toUpperCase();
}

const STATUS_CLASSES = {
  online: "bg-success",
  busy: "bg-danger",
  offline: "bg-border-strong",
} as const;

const STATUS_LABELS = {
  online: "En ligne",
  busy: "Occupé",
  offline: "Hors ligne",
} as const;

/**
 * Représentation visuelle d'une personne.
 *
 * Si l'image échoue (lien mort, hors ligne), on retombe automatiquement sur les
 * initiales : un avatar cassé est bien plus visible qu'un avatar sobre.
 */
export function Avatar({ className, size, shape, src, name, fallback, status, ...props }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const showImage = src && !failed;

  return (
    <span className="relative inline-flex">
      <span
        className={cn(avatarVariants({ size, shape }), className)}
        // L'image porte déjà son `alt` ; ce rôle ne sert qu'au cas du repli textuel.
        role={showImage ? undefined : "img"}
        aria-label={showImage ? undefined : name}
        {...props}
      >
        {showImage ? (
          <img
            src={src}
            alt={name ?? ""}
            className="size-full object-cover"
            onError={() => setFailed(true)}
          />
        ) : (
          (fallback ?? (name ? initials(name) : "?"))
        )}
      </span>

      {status && (
        <span
          title={STATUS_LABELS[status]}
          className={cn(
            "absolute right-0 bottom-0 size-2.5 rounded-full ring-2 ring-surface-raised",
            STATUS_CLASSES[status],
          )}
        >
          <span className="sr-only">{STATUS_LABELS[status]}</span>
        </span>
      )}
    </span>
  );
}

export interface AvatarGroupProps extends ComponentPropsWithRef<"div"> {
  /** Au-delà, les avatars restants sont résumés en « +N ». */
  max?: number;
}

/** Pile d'avatars superposés. Le chevauchement négatif suggère l'appartenance à un même groupe. */
export function AvatarGroup({ className, max, children, ...props }: AvatarGroupProps) {
  const items = Array.isArray(children) ? children : [children];
  const visible = max ? items.slice(0, max) : items;
  const overflow = items.length - visible.length;

  return (
    <div className={cn("flex items-center -space-x-2", className)} {...props}>
      {visible.map((child, index) => (
        <span
          key={index}
          // Au survol l'avatar passe au premier plan et se soulève : la pile
          // devient lisible sans avoir à la déplier.
          className="rounded-full ring-2 ring-surface-raised transform-gpu transition-transform duration-normal ease-spring hover:z-10 hover:-translate-y-1 hover:scale-105 hover:will-change-transform"
        >
          {child}
        </span>
      ))}
      {overflow > 0 && (
        <span className="inline-flex size-10 items-center justify-center rounded-full bg-surface-sunken text-xs font-medium text-fg-muted ring-2 ring-surface-raised">
          +{overflow}
        </span>
      )}
    </div>
  );
}

export { avatarVariants };
